import React, { useMemo, useState } from 'react';
import { Popover, Text } from '@oneaudi/unified-web-components';
import { useTranslation } from 'react-i18next';
import { FilterOption } from '../../../types';
import { ExposedFiltersTypes, FilterTypes } from '../../../constants/initialFilters';
import useAnimations from '../../../hooks/Animations/useAnimations';
import CheckboxSelector from '../../CheckboxComponent';
import FilterExposedHeader from '../FilterExposedHeader';
import { Filter, FilterSelection, FilterFrame, Disclaimer } from '../Filters.style';
import { OfferTypeListWrapper, CheckBoxContainer } from '../OfferType/OfferType.styles';

interface OfferRegionProps {
  filterType: string;
  offerRegionOptions: {
    [key: string]: FilterOption;
  };
  confirmedFilters: FilterTypes;
  availableFilters: string[];
  openedFiltersList: ExposedFiltersTypes;
  handleAddFilter: (filterType: string, value: string, isChecked?: boolean) => void;
  handleExposeFilter: (arg: string) => void;
}

const OfferRegion: React.FC<OfferRegionProps> = ({
  filterType,
  offerRegionOptions,
  confirmedFilters,
  handleAddFilter,
  handleExposeFilter,
  openedFiltersList,
  availableFilters,
}) => {
  const { t } = useTranslation();

  const selectedOfferTypesList = useMemo(() => {
    const seletedOfferTypeOptions = confirmedFilters.offerRegion;
    return seletedOfferTypeOptions
      .map((offerRegion) => offerRegionOptions[offerRegion].label)
      .join(', ');
  }, [confirmedFilters.offerRegion, offerRegionOptions]);

  const selectedOfferTypes = useMemo(() => {
    return confirmedFilters.offerRegion;
  }, [confirmedFilters.offerRegion]);

  const handleAddOfferTypeFilter = (value: string, isChecked?: boolean) => {
    handleAddFilter(filterType, value, isChecked);
  };

  const showFilter = openedFiltersList[filterType];

  const { dropdownAnimation, filtersAccordianAnimation } = useAnimations(showFilter);

  const offerRegionOffersList = Object.keys(offerRegionOptions).filter(
    (toCheck) => !offerRegionOptions[toCheck].shouldHide,
  );

  return (
    <Filter data-testid="filter-offerRegion">
      <FilterExposedHeader
        filterType={filterType}
        selectedFilterLabels={selectedOfferTypesList}
        handleExposeFilter={handleExposeFilter}
        openedFiltersList={openedFiltersList}
      />
      {dropdownAnimation(
        (props, item) =>
          item && (
            <FilterSelection style={props}>
              <FilterFrame style={filtersAccordianAnimation}>
                <Disclaimer>
                  <Text
                    variant={{
                      order: '2',
                      style: 'normal',
                    }}
                  >
                    {t('promo.filters.filterOptions.offerRegion.description')}
                  </Text>
                </Disclaimer>
                <OfferTypeListWrapper data-testid="offerType-list">
                  {offerRegionOffersList.map((filterOption) => {
                    const option = offerRegionOptions[filterOption];
                    return (
                      <CheckBoxContainer key={filterOption}>
                        <CheckboxSelector
                          inputName={option.value}
                          key={option.value}
                          onChange={() =>
                            handleAddOfferTypeFilter(
                              option.value,
                              selectedOfferTypes.includes(option.value),
                            )
                          }
                          value={option.value}
                          label={option.label}
                          ariaLabel={option.ariaLabel}
                          checked={selectedOfferTypes.includes(option.value)}
                          disabled={!availableFilters.includes(option.value)}
                          hint={option.hint}
                          filterType={filterType}
                        />
                      </CheckBoxContainer>
                    );
                  })}
                </OfferTypeListWrapper>
              </FilterFrame>
            </FilterSelection>
          ),
      )}
    </Filter>
  );
};

export default OfferRegion;
